/**
 * LanguageSettingsScreen
 * Dedicated screen for language & region settings.
 * Wraps LanguageRegionPicker with local state + Save button (batched save).
 * Save is instant (write-local-first) — navigates back immediately.
 * Navigated to from ProfileScreen.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { ScrollView, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Text } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { colors, FS_MD } from '../theme';
import { LanguageRegionPicker } from '../components/LanguageRegionPicker';
import { ScreenHeader } from '../components/ui/ScreenHeader';
import { OptionSelector, OptionPickerModal } from '../components/ui';
import { useUserPreferencesStore, getActiveCoursePreference } from '../store/userPreferencesStore';
import {
  selectCountry,
  selectSourceLocale,
  selectTargetLocale,
  selectLessonLanguage,
  selectSaveLanguagePreferences,
  selectResolvedCourse,
} from '../store/userPreferencesSelectors';
import type { TTSProvider } from '../store/userPreferencesStore';
import { useCourseStore, findCourseForLocale, getLocaleByCode } from '../store/courseStore';
import { getCountryConfig } from '../data/countryConfig';
import { detectCountry } from '../types/locale-detection';
import type { LessonLanguage } from '../types/locale';
import { useDisplayTheme } from '../hooks/useDisplayTheme';

export function LanguageSettingsScreen() {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const { bottom: bottomInset } = useSafeAreaInsets();
  const { theme, fontScale, ffs } = useDisplayTheme();

  // Store values (source of truth for "has changed" comparison)
  const storeCountry = useUserPreferencesStore(selectCountry);
  const storeVariant = useUserPreferencesStore(selectSourceLocale);
  const storeTargetLocale = useUserPreferencesStore(selectTargetLocale);
  const storeLessonLang = useUserPreferencesStore(selectLessonLanguage);
  const saveLanguagePreferences = useUserPreferencesStore(selectSaveLanguagePreferences);

  // Voice settings from active course preference
  const activePref = getActiveCoursePreference();
  const resolved = useUserPreferencesStore(selectResolvedCourse);
  const storeTtsProvider = activePref?.ttsProvider ?? 'azure-server';
  const storeVoiceName = activePref?.azureTtsVoice ?? resolved.targetLocale.speech.azure?.ttsVoice ?? '';

  // Local state (batched until Save)
  const [selectedCountry, setSelectedCountry] = useState<string | null>(storeCountry);
  const [selectedVariant, setSelectedVariant] = useState<string>(storeVariant);
  const [selectedTargetLocale, setSelectedTargetLocale] = useState<string>(storeTargetLocale);
  const [selectedLessonLang, setSelectedLessonLang] = useState<LessonLanguage>(storeLessonLang);
  const [selectedTtsProvider, setSelectedTtsProvider] = useState<TTSProvider>(storeTtsProvider);
  const [selectedVoiceName, setSelectedVoiceName] = useState<string>(storeVoiceName);

  const detectedCountry = detectCountry();

  // Available voices for the active lesson language locale
  const voiceLocale = selectedLessonLang === 'source' ? selectedVariant : selectedTargetLocale;
  const availableVoices = useMemo(() => {
    const locale = getLocaleByCode(voiceLocale);
    return locale?.speech.azure?.availableVoices ?? [];
  }, [voiceLocale]);

  // Reset voice when the voice locale changes and current voice isn't in the new list
  // Intentional: auto-select first available voice when locale changes
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (availableVoices.length > 0 && !availableVoices.some((v) => v.id === selectedVoiceName)) {
      setSelectedVoiceName(availableVoices[0].id);
    }
  }, [availableVoices, selectedVoiceName]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const hasChanges = useMemo(
    () =>
      selectedCountry !== storeCountry ||
      selectedVariant !== storeVariant ||
      selectedTargetLocale !== storeTargetLocale ||
      selectedLessonLang !== storeLessonLang ||
      selectedTtsProvider !== storeTtsProvider ||
      selectedVoiceName !== storeVoiceName,
    [selectedCountry, storeCountry, selectedVariant, storeVariant, selectedTargetLocale, storeTargetLocale, selectedLessonLang, storeLessonLang, selectedTtsProvider, storeTtsProvider, selectedVoiceName, storeVoiceName],
  );

  const countries = useCourseStore((s) => s.countries);

  const handleCountrySelect = useCallback((code: string) => {
    setSelectedCountry(code);
    const config = getCountryConfig(code, countries);
    if (!config) return;

    if (config.locales.length === 0) return;
    const newSourceLocale = config.locales[0];
    setSelectedVariant(newSourceLocale);

    // If the new locale belongs to a different course, update target locale and reset voice to default
    const match = findCourseForLocale(newSourceLocale);
    if (match) {
      setSelectedTargetLocale(match.defaultTargetLocale);
      setSelectedVoiceName(getLocaleByCode(match.defaultTargetLocale)?.speech.azure?.ttsVoice ?? '');
    }
  }, [countries]);

  const handleVariantSelect = useCallback((variant: string) => {
    setSelectedVariant(variant);
  }, []);

  const handleTargetLocaleChange = useCallback((locale: string) => {
    setSelectedTargetLocale(locale);
  }, []);

  const handleLessonLanguageChange = useCallback((lang: LessonLanguage) => {
    setSelectedLessonLang(lang);
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedCountry) return;
    await saveLanguagePreferences(
      selectedCountry,
      selectedVariant,
      selectedTargetLocale,
      selectedLessonLang,
      { azureTtsVoice: selectedVoiceName, ttsProvider: selectedTtsProvider, sttProvider: activePref?.sttProvider ?? 'azure' },
    );
    navigation.goBack();
  }, [selectedCountry, selectedVariant, selectedTargetLocale, selectedLessonLang, selectedVoiceName, selectedTtsProvider, activePref?.sttProvider, saveLanguagePreferences, navigation]);

  return (
    <View style={{ flex: 1, backgroundColor: theme.background }}>
      <ScreenHeader title={t('profile.learning.languageSettings')} />
      <ScrollView
        testID="language-settings-screen"
        style={{ flex: 1 }}
        contentContainerStyle={{ padding: 24, paddingBottom: 8 + bottomInset, gap: 20 }}
      >
        <LanguageRegionPicker
        country={selectedCountry}
        sourceLocale={selectedVariant}
        targetLocale={selectedTargetLocale}
        lessonLanguage={selectedLessonLang}
        detectedCountry={detectedCountry}
        onCountrySelect={handleCountrySelect}
        onVariantSelect={handleVariantSelect}
        onTargetLocaleChange={handleTargetLocaleChange}
        onLessonLanguageChange={handleLessonLanguageChange}
      />

      {/* Voice quality: Device (expo-speech) vs Neural. The Neural option
          ('azure-server') synthesizes backend-side via POST /api/speech/tts. */}
      <OptionSelector
        label={t('profile.learning.ttsVoiceTitle')}
        options={[
          { value: 'device' as TTSProvider, label: t('profile.learning.ttsVoiceDevice') },
          { value: 'azure-server' as TTSProvider, label: t('profile.learning.ttsVoiceAzure') },
        ]}
        value={selectedTtsProvider}
        onChange={setSelectedTtsProvider}
        testID="language-settings-tts-provider"
      />

      {/* Voice selection (both Azure providers use the same voice catalog) */}
      {selectedTtsProvider !== 'device' && (
        <OptionPickerModal
          label={t('profile.learning.ttsVoiceSelectLabel')}
          modalTitle={t('profile.learning.ttsVoiceSelectLabel')}
          options={availableVoices.map((v) => ({ value: v.id, label: v.label }))}
          value={selectedVoiceName}
          onChange={setSelectedVoiceName}
          testID="language-settings-tts-voice"
        />
      )}

      {/* Save button */}
      <TouchableOpacity
        testID="language-settings-save"
        accessibilityLabel={t('common.save')}
        accessibilityRole="button"
        accessibilityState={{ disabled: !hasChanges }}
        onPress={handleSave}
        activeOpacity={0.7}
        disabled={!hasChanges}
        style={{
          marginTop: 32,
          backgroundColor: hasChanges ? colors.primary : theme.border,
          borderRadius: 12,
          paddingVertical: 14,
          alignItems: 'center',
        }}
      >
        <Text
          allowFontScaling={false}
          style={{
            fontSize: FS_MD * fontScale,
            fontWeight: '600',
            color: hasChanges ? '#FFFFFF' : theme.textSecondary,
            ...ffs,
          }}
        >
          {t('common.save')}
        </Text>
      </TouchableOpacity>
      </ScrollView>
    </View>
  );
}
